import { UserRound } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage, Skeleton } from "@ceedero/ui";

import { resolveAccountInitials, resolveAvatarUrl } from "@/account/utils/avatar";

import type { AccountProfile } from "@/account/hooks/use-account";

/**
 * The account's picture, or the nearest thing to one.
 *
 * Three states in one component so they cannot be drawn differently in the two
 * places this appears: a picture, the initials of whatever the account is
 * called, and a person glyph for an account with neither. The image carries an
 * empty alt because whatever sits beside it already names the account —
 * describing it would have a screen reader say the name twice.
 */
export const AccountAvatar = ({
  className,
  isLoading = false,
  profile,
}: {
  className?: string;
  isLoading?: boolean;
  profile: AccountProfile | undefined;
}) => {
  if (isLoading) {
    return <Skeleton className={className} data-slot="account-avatar-skeleton" />;
  }

  const source = resolveAvatarUrl(profile);
  const initials = resolveAccountInitials(profile);

  return (
    <Avatar className={className}>
      {source !== undefined && <AvatarImage src={source} alt="" />}
      <AvatarFallback className="bg-primary-fill text-primary-fill-foreground shadow-raised">
        {initials ?? <UserRound aria-hidden />}
      </AvatarFallback>
    </Avatar>
  );
};
